import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useFireAuth } from '../FireAuthContext';
import DelayRender from './DelayRender';
import Rewards from './Rewards';
import { FaDice, FaTrophy, FaChartLine, FaGift, FaShoppingCart, FaBullhorn } from 'react-icons/fa';
import { getFirestore, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import Notices from './Notices';
import MembershipTrophy from './MembershipTrophy';
import './PortalHome.css';

const PortalHome = () => {
  const { user, updateUser } = useFireAuth();
  const [topPlayers, setTopPlayers] = useState([]);
  const [notices, setNotices] = useState([]);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchTopPlayers = async () => {
      const db = getFirestore();
      const usersCollection = collection(db, 'users');
      const q = query(usersCollection, orderBy('money', 'desc'), limit(3));
      const querySnapshot = await getDocs(q);
      const players = [];
      querySnapshot.forEach((doc) => {
        players.push({ ...doc.data(), id: doc.id });
      });
      setTopPlayers(players);
    };

    const fetchNotices = async () => {
      const db = getFirestore();
      const noticesCollection = collection(db, 'notices');
      const q = query(noticesCollection, orderBy('timestamp', 'desc'), limit(5));
      const querySnapshot = await getDocs(q);
      const noticesList = [];
      querySnapshot.forEach((doc) => {
        noticesList.push({ ...doc.data(), id: doc.id });
      });
      setNotices(noticesList);
    };

    fetchTopPlayers();
    fetchNotices();
  }, []);

  useEffect(() => {
    updateUser();
  }, [updateUser]);

  const formatBalance = useCallback((balance) => {
    return balance % 1 === 0 ? balance.toFixed(0) : balance.toFixed(2);
  }, []);

  const openNoticeModal = (notice) => {
    setSelectedNotice(notice);
    setIsModalOpen(true);
  };

  const closeNoticeModal = () => {
    setSelectedNotice(null);
    setIsModalOpen(false);
  };

  if (!user) {
    return <div className="portal-section">Loading...</div>;
  }

  return (
    <div className="portal-section">
      <div className="portal-container">
        <p className="home-greeting">Welcome back, {user.name}!</p>
        
        <div className="home-grid">
          <div className="home-card featured-games">
            <h3><FaDice /> Featured Games</h3>
            <ul>
              <li><Link to="/portal/raffle">Weekly Raffle</Link></li>
              <li><Link to="/portal/blackjack">Blackjack</Link></li>
              <li><Link to="/portal/spin">Spin the Wheel</Link></li>
            </ul>
          </div>

          <div className="home-card user-stats">
            <h3><FaChartLine /> Your Stats</h3>
            <p><strong>Balance:</strong> 💴 {user.money !== undefined ? formatBalance(user.money) : 'Loading...'}</p>
            <p><strong>Games Played:</strong> {user.gamesPlayed !== undefined ? user.gamesPlayed : 'Loading...'}</p>
            <p><strong>Member since:</strong> {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'Not available'}</p>
            <p><strong>Membership Status:</strong> <MembershipTrophy status={user.membershipStatus} /> {user.membershipStatus}</p>
          </div>

          <div className="home-card current-promotions">
            <h3><FaGift /> Market</h3>
            <p>📈 The market is open!</p>
            <Link to="/portal/market" className="btn-shop">Visit Market</Link>
          </div>

          <div className="home-card leaderboard">
            <h3><FaTrophy /> Top Players</h3>
            <ol>
              {topPlayers.map((player, index) => (
                <li key={player.id}>{player.name} - 💴 {formatBalance(player.money)}</li>
              ))}
            </ol>
          </div>

          <div className="home-card daily-rewards">
            <Rewards onRewardClaimed={updateUser} />
          </div>

          <div className="home-card shop-info">
            <h3><FaShoppingCart /> Shop</h3>
            <p>Check out our latest items and exclusive deals! You can now sell your items too!</p>
            <Link to="/portal/shop" className="btn-shop">Visit Shop</Link>
          </div>

          <div className="home-card notice-board">
            <h3><FaBullhorn /> Noticeboard</h3>
            {notices.length > 0 ? (
              <ul>
                {notices.map((notice) => (
                  <li key={notice.id}>
                    <button 
                      className="notice-title-button" 
                      onClick={() => openNoticeModal(notice)}
                    >
                      <strong>{notice.title}</strong>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No notices available</p>
            )}
          </div>
        </div>
      </div>
      <Notices 
        isOpen={isModalOpen} 
        onRequestClose={closeNoticeModal} 
        selectedNotice={selectedNotice} 
      />
    </div>
  );
};

export default DelayRender(PortalHome, 300);
